'use client'

import { useCallback, useMemo } from 'react'
import Image from 'next/image'
import { nextImageHelper } from 'utils/next-image-css-helper'
import cn from 'utils/classnames'

export interface Option {
  id: string
  option: string
  icon?: string
  url?: string
  nextQuestion?: string
}

export const OptionButton = ({
  option,
  selected,
  onSelect,
}: {
  option: Option
  selected: boolean
  onSelect: (option: Option) => void
}) => {
  const handleSelect = useCallback(() => onSelect(option), [onSelect, option])

  const [mainText, subText] = useMemo(() => {
    if (option.option.includes('(')) {
      const [first, ...rest] = option.option.split(' ')
      return [first, rest.join(' ')]
    }
    return [option.option, '']
  }, [option.option])

  return (
    <button
      type="button"
      onClick={handleSelect}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-4 rounded-sm border px-4 py-3 transition-colors',
        selected
          ? 'bg-primary-light-bg border-primary-bright'
          : 'border-[#E6E6E6] hover:bg-primary-light-bg hover:border-primary-bright',
        option.icon && 'py-0',
      )}
    >
      {option.icon && (
        <Image
          src={option.icon}
          alt={`${mainText} icon`}
          height={48}
          width={48}
          style={nextImageHelper.intrinsic}
        />
      )}
      <div className="flex items-center gap-1 text-card-gray">
        <p
          className={cn(
            'text-sm sm:text-lg',
            (subText || mainText.toLowerCase() === "don't know") &&
              'font-bold text-lg sm:text-xl',
          )}
        >
          {mainText}
        </p>
        {subText && <p className="text-lg sm:text-xl">{subText}</p>}
      </div>
    </button>
  )
}
